import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import { useComponentTranslation } from "@/services/i18n";
import { FaDownload, FaPrint, FaQrcode, FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";
import QRCode from "qrcode";
import { CANDIDATE_URL } from "@/config";
import API from "@/services/api";
import { FiCopy } from "react-icons/fi";
import PublishToggle from "./components/publish-toggle";

const translations = {
  en: {
    title: "QR Code",
    description: "Generate a QR code for your job posting to share in physical locations",
    download: "Download QR Code",
    print: "Print QR Code",
    generating: "Generating...",
    downloaded: "QR Code downloaded",
    instructions: "Scan this QR code to apply for the position",
    publish: "Publish",
    unpublish: "Unpublish",
    post_published: "Post published successfully",
    post_unpublished: "Post unpublished successfully",
    post_not_enabled: "Failed to publish post",
    back: "Back to channels",
    copy_link: "Copy link",
    link_copied: "Link copied to clipboard",
    print_title: "QR Code - Job",
  },
  fr: {
    title: "QR Code",
    description: "Générez un QR code pour votre offre d'emploi à partager dans des lieux physiques",
    download: "Télécharger le QR Code",
    print: "Imprimer le QR Code",
    generating: "Génération...",
    downloaded: "QR Code téléchargé",
    instructions: "Scannez ce QR code pour postuler au poste",
    publish: "Publier",
    unpublish: "Dépublier",
    post_published: "Publication publiée avec succès",
    post_unpublished: "Publication dépubliée avec succès",
    post_not_enabled: "Échec de la publication",
    back: "Retour aux canaux",
    copy_link: "Copier le lien",
    link_copied: "Lien copié dans le presse-papiers",
    print_title: "QR Code - Offre",
  },
};

export default function QRCodeChannel({ job, setJob, posts = [], setPosts }) {
  const { t } = useComponentTranslation(translations);
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [qrCode, setQrCode] = useState("");
  const [loading, setLoading] = useState(true);
  const channelSlug = "qr-code";

  useEffect(() => {
    fetchChannel();
  }, []);

  const fetchChannel = async () => {
    try {
      setLoading(true);
      const channelResponse = await API.post(`/channel/search`, {
        slug: channelSlug,
        limit: 1,
      });
      if (channelResponse.ok && channelResponse.data?.[0]) {
        setChannel(channelResponse.data[0]);
      }
    } catch (e) {
      console.error("Error loading channel:", error);
    } finally {
      setLoading(false);
    }
  };

  // Find job post from posts array
  const jobPost = channel ? posts.find((post) => post.channel_id === channel._id) : null;

  const shareLink = useMemo(() => {
    if (!job?.organization_slug || !jobPost?._id) return "";
    return `https://jobego.co/companies/${job.organization_slug}/jobs/${jobPost._id}`;
  }, [job, jobPost]);

  const generateQRCode = useCallback(async () => {
    if (!shareLink) return;
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(shareLink, {
        width: 300,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });
      setQrCode(qrCodeDataUrl);
    } catch (e) {
      console.error("Error generating QR code:", error);
    }
  }, [shareLink]);

  useEffect(() => {
    if (jobPost) {
      generateQRCode();
    }
  }, [jobPost, generateQRCode]);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.download = `job-${jobId}-qrcode.png`;
    link.href = qrCode;
    link.click();
    toast.success(t("downloaded"));
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>${t("print_title")} ${jobId}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
            }
            .container {
              text-align: center;
              padding: 40px;
            }
            img {
              max-width: 300px;
              margin: 20px 0;
            }
            h1 {
              font-size: 24px;
              margin-bottom: 10px;
            }
            p {
              font-size: 16px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>${t("title")}</h1>
            <img src="${qrCode}" alt="QR Code" />
            <p>${t("instructions")}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back button */}
      <button onClick={() => navigate("..")} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <FaArrowLeft className="w-4 h-4" />
        {t("back")}
      </button>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center shrink-0">
            <FaQrcode className="w-8 h-8 text-purple-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-2xl font-bold text-gray-900">{channel?.name || t("title")}</h3>
              {channel && <PublishToggle job={job} channel={channel} posts={posts} setPosts={setPosts} />}
            </div>

            <p className="text-sm text-gray-600 mb-6">{t("description")}</p>

            {jobPost && (
              <>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-4 flex justify-center">
                  {!qrCode ? (
                    <div className="flex flex-col items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-y-primary border-x-transparent mb-2" />
                      <p className="text-sm text-gray-600">{t("generating")}</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <img src={qrCode} alt="QR Code" className="w-64 h-64" />
                      <p className="text-sm text-gray-600 mt-4">{t("instructions")}</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      if (navigator && navigator.clipboard) {
                        await navigator.clipboard.writeText(shareLink);
                        toast.success(t("link_copied"));
                      }
                    }}
                    className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700 mr-2"
                    type="button"
                    title={t("copy_link")}
                  >
                    <FiCopy />
                    {t("copy_link")}
                  </button>

                  <button onClick={handleDownload} disabled={!qrCode} className="btn btn-primary flex items-center gap-2">
                    <FaDownload className="w-4 h-4" />
                    {t("download")}
                  </button>
                  <button onClick={handlePrint} disabled={!qrCode} className="btn btn-secondary flex items-center gap-2">
                    <FaPrint className="w-4 h-4" />
                    {t("print")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
